import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CheckCircle, X, ArrowLeft, Crown, Zap, Star } from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import PublicNav from '../../components/PublicNav';
import Footer from '../../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InvestorPricing() {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/subscription/available-plans?role=investor`);
      
      const apiPlans = response.data.plans;
      const mappedPlans = {
        free: {
          name: apiPlans.find(p => p.plan_name === 'Investor Free')?.plan_name || 'Free',
          price: 0,
          description: 'Browse and save ventures',
          features: [
            { text: 'Limited venture browsing (15/month)', included: true },
            { text: 'Save ventures to watchlist', included: true },
            { text: 'View venture summaries', included: true },
            { text: '1 pitch deck preview per month (view-only)', included: true },
            { text: '1 message initiation per month', included: true },
            { text: 'NDA access to pitch decks', included: false },
            { text: 'Pitch sessions', included: false },
            { text: 'Advanced filters', included: false },
          ]
        },
        pro: {
          name: apiPlans.find(p => p.plan_name === 'Investor Pro')?.plan_name || 'Investor Pro',
          monthlyPrice: apiPlans.find(p => p.plan_name === 'Investor Pro')?.monthly_price_usd || 249,
          annualPrice: apiPlans.find(p => p.plan_name === 'Investor Pro')?.yearly_price_usd || 2490,
          savings: '2 months free',
          description: 'Full access to venture discovery',
          popular: true,
          features: [
            { text: 'Everything in Free', included: true },
            { text: 'Unlimited venture discovery', included: true },
            { text: 'Direct messaging with founders', included: true },
            { text: 'NDA access to pitch decks', included: true },
            { text: 'Schedule pitch sessions', included: true },
            { text: 'Advanced filters (stage, geo, funding)', included: true },
            { text: 'Deal tracking dashboard', included: true },
            { text: 'Email notifications', included: true },
          ]
        },
        premium: {
          name: apiPlans.find(p => p.plan_name === 'Investor Premium')?.plan_name || 'Investor Premium',
          monthlyPrice: apiPlans.find(p => p.plan_name === 'Investor Premium')?.monthly_price_usd || 799,
          annualPrice: apiPlans.find(p => p.plan_name === 'Investor Premium')?.yearly_price_usd || 7990,
          savings: '2 months free',
          description: 'White-glove service for serious investors',
          features: [
            { text: 'Everything in Pro', included: true },
            { text: 'Curated weekly deal flow reports', included: true },
            { text: 'Early access to new startups (48h head start)', included: true },
            { text: 'Private pitch rooms', included: true },
            { text: 'Dedicated account manager', included: true },
            { text: 'Portfolio tracking tools', included: true },
            { text: 'Founder background checks', included: true },
            { text: 'Custom deal sourcing', included: true },
          ]
        }
      };
      
      setPlans(mappedPlans);
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPrice = (plan) => {
    if (!plans) return '$0';
    if (plan === 'free') return '$0';
    const price = billingCycle === 'monthly' ? plans[plan].monthlyPrice : Math.round(plans[plan].annualPrice / 12);
    return `$${price}`;
  };

  if (loading || !plans) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-slate-300">Loading plans...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="investor-pricing-page">
      <PublicNav currentPage="pricing" />

      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <Link to="/for-investors" className="inline-flex items-center text-blue-400 hover:text-blue-300 mb-6 text-sm">
            <ArrowLeft className="mr-2" size={16} />
            Back to For Investors
          </Link>
          <div className="inline-block px-4 py-2 bg-blue-500/20 text-blue-400 rounded-full text-sm font-medium mb-6 ml-4">
            FOR INVESTORS
          </div>
          <h1 className="text-5xl font-bold text-slate-100 mb-4">Pricing for Venture Discovery</h1>
          <p className="text-xl text-slate-400 mb-8">Browse free, unlock full access when you find aligned opportunities</p>
          
          <div className="inline-flex items-center gap-4 glass-card p-2 rounded-lg">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-md transition-all ${
                billingCycle === 'monthly' ? 'bg-blue-500 text-slate-950 font-semibold' : 'text-slate-400'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-6 py-2 rounded-md transition-all flex items-center gap-2 ${
                billingCycle === 'annual' ? 'bg-blue-500 text-slate-950 font-semibold' : 'text-slate-400'
              }`}
            >
              Annual
              <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">Save 20%</span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {/* Free Plan */}
          <Card className="glass-card p-8 hover:border-blue-500/50 transition-all">
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-slate-100 mb-2">{plans.free.name}</h3>
              <div className="flex items-baseline gap-2 mb-3">
                <span className="text-5xl font-bold text-blue-500">$0</span>
                <span className="text-slate-400">forever</span>
              </div>
              <p className="text-slate-400">{plans.free.description}</p>
            </div>
            <ul className="space-y-3 mb-8">
              {plans.free.features.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  {feature.included ? (
                    <CheckCircle className="text-blue-500 flex-shrink-0 mt-0.5" size={20} />
                  ) : (
                    <X className="text-blue-700 flex-shrink-0 mt-0.5" size={20} />
                  )}
                  <span className={feature.included ? 'text-slate-300' : 'text-blue-700'}>{feature.text}</span>
                </li>
              ))}
            </ul>
            <Button 
              onClick={() => navigate('/register?role=investor&plan=free')} 
              className="btn-secondary-dark w-full"
              data-testid="select-free"
            >
              Start Browsing
            </Button>
          </Card>

          {/* Pro Plan */}
          <Card className="glass-card p-8 border-blue-500 relative">
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <div className="bg-blue-500 text-slate-950 px-4 py-1 rounded-full text-sm font-bold flex items-center gap-2">
                <Zap size={14} />
                MOST POPULAR
              </div>
            </div>
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-slate-100 mb-2">{plans.pro.name}</h3>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-5xl font-bold text-blue-500">{getPrice('pro')}</span>
                <span className="text-slate-400">/{billingCycle === 'monthly' ? 'mo' : 'mo'}</span>
              </div>
              {billingCycle === 'annual' && (
                <div className="text-sm text-green-400 mb-3">{plans.pro.savings}</div>
              )}
              <p className="text-slate-400">{plans.pro.description}</p>
            </div>
            <ul className="space-y-3 mb-8">
              {plans.pro.features.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <CheckCircle className="text-blue-500 flex-shrink-0 mt-0.5" size={20} />
                  <span className="text-slate-300">{feature.text}</span>
                </li>
              ))}
            </ul>
            <Button 
              onClick={() => navigate(`/register?role=investor&plan=pro&billing=${billingCycle}`)} 
              className="btn-primary w-full"
              data-testid="select-pro"
            >
              Upgrade to Pro
            </Button>
          </Card>

          {/* Premium Plan */}
          <Card className="glass-card p-8 hover:border-blue-500/50 transition-all relative overflow-hidden">
            <div className="absolute top-4 right-4">
              <Star className="text-blue-500" size={24} />
            </div>
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-slate-100 mb-2">{plans.premium.name}</h3>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-5xl font-bold text-blue-500">{getPrice('premium')}</span>
                <span className="text-slate-400">/{billingCycle === 'monthly' ? 'mo' : 'mo'}</span>
              </div>
              {billingCycle === 'annual' && (
                <div className="text-sm text-green-400 mb-3">{plans.premium.savings}</div>
              )}
              <p className="text-slate-400">{plans.premium.description}</p>
            </div>
            <ul className="space-y-3 mb-8">
              {plans.premium.features.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <CheckCircle className="text-blue-500 flex-shrink-0 mt-0.5" size={20} />
                  <span className="text-slate-300">{feature.text}</span>
                </li>
              ))}
            </ul>
            <Button 
              onClick={() => navigate(`/register?role=investor&plan=premium&billing=${billingCycle}`)} 
              className="btn-primary w-full"
              data-testid="select-premium"
            >
              Upgrade to Premium
            </Button>
          </Card>
        </div>

        <Card className="glass-card p-12 text-center">
          <h2 className="text-3xl font-bold text-slate-100 mb-4">Questions About Pricing?</h2>
          <p className="text-slate-400 mb-6">All paid plans include verification badge and priority support</p>
          <div className="flex items-center justify-center gap-4">
            <Button onClick={() => navigate('/register?role=investor')} className="btn-primary">
              Start Browsing Free
            </Button>
            <Button onClick={() => navigate('/faq')} className="btn-secondary-dark">
              View FAQ
            </Button>
          </div>
        </Card>
      </div>

      <Footer />
    </div>
  );
}
